import { useMemo, type ReactNode } from 'react'
import type { UseQueryResult } from '@tanstack/react-query'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { api, useApi } from '../api/queries'
import type { ApiError } from '../api/problem'
import type { components } from '../api/schema'
import type {
  BanReplay,
  ConstructorDetail,
  ConstructorDriver,
  ConstructorStats,
  DriverDetail,
  DriverSeason,
  DriverStats,
  ResultItem,
} from '../api/types'
import {
  ConstructorDriversTable,
  ConstructorStatsTable,
  DriverStatsTable,
  NameFilter,
} from '../components/CareerTables'
import { DataState } from '../components/DataState'
import { CareerProgression, type Removals } from '../chart/SeasonProgression'
import { formatPoints } from '../lib/decimal'
import { useUniversePath } from '../lib/universe'

type ResultPage = components['schemas']['Page_ResultItem_']

export function DriversPage() {
  const drivers = useApi<DriverStats[]>(['stats', 'drivers'], () =>
    api.GET('/api/v1/stats/drivers', {}),
  )
  return (
    <section>
      <h1>Drivers</h1>
      <p className="muted">Every driver who entered a World Championship Grand Prix.</p>
      <NameFilter label="Search drivers" />
      <DataState query={drivers} empty="No drivers.">
        {(rows) => <DriverStatsTable rows={rows} />}
      </DataState>
    </section>
  )
}

export function ConstructorsPage() {
  const constructors = useApi<ConstructorStats[]>(['stats', 'constructors'], () =>
    api.GET('/api/v1/stats/constructors', {}),
  )
  return (
    <section>
      <h1>Constructors</h1>
      <p className="muted">
        Titles are constructors’ championships, held since 1958; points are championship points.
      </p>
      <NameFilter label="Search constructors" />
      <DataState query={constructors} empty="No constructors.">
        {(rows) => <ConstructorStatsTable rows={rows} />}
      </DataState>
    </section>
  )
}

export function DriverPage() {
  const { slug = '' } = useParams()
  const driver = useApi<DriverDetail>(['driver', slug], () =>
    api.GET('/api/v1/drivers/{slug}', { params: { path: { slug } } }),
  )
  const seasons = useApi<DriverSeason[]>(['driver-seasons', slug], () =>
    api.GET('/api/v1/drivers/{slug}/seasons', { params: { path: { slug } } }),
  )

  return (
    <section>
      <DataState query={driver}>
        {(data) => (
          <>
            <h1>{data.name}</h1>
            <p className="muted">
              {data.nationality?.name ?? ''}
              {data.date_of_birth ? ` · born ${data.date_of_birth}` : ''}
              {data.permanent_number ? ` · #${data.permanent_number}` : ''}
            </p>
            <Career entity={data} />
          </>
        )}
      </DataState>
      <h2 className="section-title">Season by season</h2>
      <DataState query={seasons} empty="No results are recorded.">
        {(rows) => <DriverSeasons rows={rows} />}
      </DataState>
    </section>
  )
}

/**
 * One row per season and team; a season's championship spans its rows.
 * A replayed history rescores championships, not results, so it has no
 * points per team and the column goes.
 */
function DriverSeasons({ rows }: { rows: DriverSeason[] }) {
  const to = useUniversePath()
  const scored = rows.some((row) => row.points != null)
  return (
    <div className="table-scroll">
      <table className="grid">
        <thead>
          <tr>
            <th scope="col">Season</th>
            <th scope="col">Team</th>
            <th scope="col" className="num" title="Grands Prix entered">
              Entries
            </th>
            <th scope="col" className="num">
              Starts
            </th>
            <th scope="col" className="num">
              Wins
            </th>
            <th scope="col" className="num">
              Podiums
            </th>
            <th scope="col" className="num">
              Poles
            </th>
            <th scope="col" className="num">
              Fastest laps
            </th>
            <th scope="col" className="num" title="Retirements">
              DNF
            </th>
            <th scope="col" className="num" title="Best finish in a Grand Prix">
              Best
            </th>
            {scored ? (
              <th scope="col" className="num" title="Scored with this team, sprints included">
                Points
              </th>
            ) : null}
            <th scope="col" className="num" title="Position in the drivers’ championship">
              Champ.
            </th>
            <th scope="col" className="num" title="Points counted for the championship">
              Champ. pts
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const first = rows[index - 1]?.year !== row.year
            const span = rows.filter((other) => other.year === row.year).length
            return (
              <tr key={`${row.year}-${row.constructor.slug}`}>
                {first ? (
                  <td rowSpan={span}>
                    <Link to={to(`/seasons/${row.year}`)}>{row.year}</Link>
                  </td>
                ) : null}
                <td>
                  <Link to={to(`/constructors/${row.constructor.slug}`)}>
                    {row.constructor.name}
                  </Link>
                </td>
                <td className="num">{row.entries}</td>
                <td className="num">{row.starts}</td>
                <td className="num">{row.wins}</td>
                <td className="num">{row.podiums}</td>
                <td className="num">{row.poles}</td>
                <td className="num">{row.fastest_laps}</td>
                <td className="num">{row.retirements}</td>
                <td className="num">{row.best_finish ?? '—'}</td>
                {scored ? <td className="num">{formatPoints(row.points, '0')}</td> : null}
                {first ? (
                  <>
                    <td className="num" rowSpan={span}>
                      {row.championship_position ?? '—'}
                    </td>
                    <td className="num" rowSpan={span}>
                      {formatPoints(row.championship_points)}
                    </td>
                  </>
                ) : null}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export function ConstructorPage() {
  const { slug = '' } = useParams()
  const [params] = useSearchParams()
  const year = toYear(params.get('year'))
  const constructor = useApi<ConstructorDetail>(['constructor', slug], () =>
    api.GET('/api/v1/constructors/{slug}', { params: { path: { slug } } }),
  )
  const drivers = useApi<ConstructorDriver[]>(['constructor-drivers', slug], () =>
    api.GET('/api/v1/constructors/{slug}/drivers', { params: { path: { slug } } }),
  )
  const results = useApi<ResultPage>(['constructor-results', slug, year], () =>
    api.GET('/api/v1/constructors/{slug}/results', {
      params: { path: { slug }, query: { limit: 200, ...(year ? { year } : {}) } },
    }),
  )

  return (
    <section>
      <DataState query={constructor}>
        {(data) => (
          <>
            <h1>{data.name}</h1>
            <p className="muted">
              {data.full_name}
              {data.country?.name ? ` · ${data.country.name}` : ''}
            </p>
            <Career entity={data} />
          </>
        )}
      </DataState>
      <h2 className="section-title">Drivers</h2>
      <DataState query={drivers} empty="No drivers are recorded.">
        {(rows) => <ConstructorDriversTable rows={rows} />}
      </DataState>
      <h2 className="section-title">Results by season</h2>
      <SeasonFilter year={year} />
      <DataState
        query={results}
        empty={`No results are recorded${year ? ` for ${year}` : ''}.`}
      >
        {(page) => (
          <>
            <CareerProgression kind="constructors" slug={slug} years={yearsOf(page.items)} />
            <SeasonResults
              rows={page.items}
              total={page.pagination.total}
              kind="constructors"
            />
          </>
        )}
      </DataState>
    </section>
  )
}

/**
 * A category that replays history -- F2's rule, F1.5's, the bans, a points
 * table -- as much of it as a driver's page needs to be drawn inside it.
 */
export type Universe = {
  /** Its first page: `/f2`, `/ban`. The driver's page is `${path}/drivers/:slug`. */
  path: string
  /** Ends the title, as on its other pages: "under Formula 2’s rule". */
  suffix: string
  use: () => {
    /** Whether anything has been chosen yet; until then no page asks the server. */
    chosen: boolean
    /** The choice as a `?…` string, carried from page to page. */
    search: string
    summary: ReactNode
    /** Every career in it: the list itself (F2, F1.5) or a whole replay (bans, points). */
    careers: UseQueryResult<DriverStats[] | BanReplay, ApiError>
    /** How `useApi` asks for one driver's seasons in it. */
    driverSeasons: (slug: string) => SeasonsRequest
  }
  /** What one season leaves out and scores on; undefined while it is being found. */
  useRemovals: (year: number) => Removals | undefined
}

export type SeasonsRequest = [
  key: readonly unknown[],
  fetcher: () => Promise<{ data?: DriverSeason[]; error?: unknown; response: Response }>,
]

/** One driver as a category replays them, in the shape of their real page. */
export function UniverseDriverPage({ universe }: { universe: Universe }) {
  const { slug = '' } = useParams()
  const { chosen, summary, careers, driverSeasons } = universe.use()
  const driver = useApi<DriverDetail>(['driver', slug], () =>
    api.GET('/api/v1/drivers/{slug}', { params: { path: { slug } } }),
  )
  const seasons = useApi(...driverSeasons(slug), { enabled: chosen })

  return (
    <section>
      <DataState query={driver}>
        {(data) => (
          <>
            <h1>
              {data.name} {universe.suffix}
            </h1>
            <p className="muted">
              {data.nationality?.name ?? ''}
              {data.date_of_birth ? ` · born ${data.date_of_birth}` : ''} ·{' '}
              <Link to={`/drivers/${slug}`}>As it really happened →</Link>
            </p>
          </>
        )}
      </DataState>
      {summary}
      {chosen ? (
        <DataState query={careers}>
          {(data) => {
            const row = (Array.isArray(data) ? data : data.drivers).find(
              (entry) => entry.driver.slug === slug,
            )
            if (!row?.last_year) {
              return <p className="state state--empty">Never took a start {universe.suffix}.</p>
            }
            return (
              <>
                <Career
                  entity={{
                    total_championship_wins: row.titles,
                    total_race_starts: row.starts,
                    total_race_wins: row.wins,
                    total_podiums: row.podiums,
                    total_pole_positions: row.poles,
                    total_points: row.points,
                  }}
                />
                <h2 className="section-title">Season by season</h2>
                <DataState query={seasons} empty="No results are recorded.">
                  {(rows) => <DriverSeasons rows={rows} />}
                </DataState>
              </>
            )
          }}
        </DataState>
      ) : null}
    </section>
  )
}

/** One constructor as a category replays it: the career if it has one, then a season of it. */
export function UniverseConstructorPage({ universe }: { universe: Universe }) {
  const { slug = '' } = useParams()
  const [params] = useSearchParams()
  const { chosen, summary, careers } = universe.use()
  const constructor = useApi<ConstructorDetail>(['constructor', slug], () =>
    api.GET('/api/v1/constructors/{slug}', { params: { path: { slug } } }),
  )
  // F2's and F1.5's rules replay drivers' careers only: the season to open on comes from the record.
  const real = useApi<ConstructorStats[]>(['stats', 'constructors'], () =>
    api.GET('/api/v1/stats/constructors', {}),
  )

  return (
    <section>
      <DataState query={constructor}>
        {(data) => (
          <>
            <h1>
              {data.name} {universe.suffix}
            </h1>
            <p className="muted">
              {data.full_name} ·{' '}
              <Link to={`/constructors/${slug}`}>As it really happened →</Link>
            </p>
          </>
        )}
      </DataState>
      {summary}
      {chosen ? (
        <DataState query={careers}>
          {(data) => {
            const replayed = Array.isArray(data)
              ? undefined
              : data.constructors.find((entry) => entry.constructor.slug === slug)
            if (!Array.isArray(data) && !replayed) {
              return <p className="state state--empty">Never took a start {universe.suffix}.</p>
            }
            const last =
              replayed?.last_year ??
              real.data?.find((entry) => entry.constructor.slug === slug)?.last_year
            const year = toYear(params.get('year')) ?? last
            return (
              <>
                {replayed ? (
                  <Career
                    entity={{
                      total_championship_wins: replayed.titles,
                      total_race_starts: replayed.starts,
                      total_race_wins: replayed.wins,
                      total_podiums: replayed.podiums,
                      total_pole_positions: replayed.poles,
                      total_points: replayed.points,
                    }}
                  />
                ) : null}
                <h2 className="section-title">Results by season</h2>
                <SeasonFilter year={year ?? null} />
                {year ? (
                  <UniverseSeason
                    universe={universe}
                    kind="constructors"
                    slug={slug}
                    year={year}
                  />
                ) : (
                  <DataState query={real}>{() => null}</DataState>
                )}
              </>
            )
          }}
        </DataState>
      ) : null}
    </section>
  )
}

/**
 * One season's results as a category replays them, once it is known whom it
 * leaves out: the removed are gone, everyone behind moved up and rescored.
 * A banned Grand Prix's rows, which the route keeps, are dropped here as the
 * results matrix drops its column.
 */
export function useUniverseResults(universe: Universe, year: number) {
  const removals = universe.useRemovals(year)
  const results = useApi<ResultItem[]>(
    ['season-results', year, removals],
    () =>
      api.GET('/api/v1/seasons/{year}/results', {
        params: { path: { year }, query: removals ?? {} },
      }),
    { enabled: removals !== undefined },
  )
  const skipped = (removals?.exclude_rounds ?? '').split(',').filter(Boolean).map(Number)
  const rows = removals
    ? results.data?.filter((row) => !skipped.includes(row.round))
    : undefined
  return { removals, results, rows, skipped }
}

/** One season of a driver or a constructor inside a category. */
function UniverseSeason({
  universe,
  kind,
  slug,
  year,
}: {
  universe: Universe
  kind: 'drivers' | 'constructors'
  slug: string
  year: number
}) {
  const { removals, results, rows: season } = useUniverseResults(universe, year)
  const rows = season?.filter(
    (row) => (kind === 'drivers' ? row.driver.slug : row.constructor.slug) === slug,
  )

  if (!removals || !rows) return <DataState query={results}>{() => null}</DataState>
  if (rows.length === 0) {
    return (
      <p className="state state--empty">
        No results in {year} {universe.suffix}.
      </p>
    )
  }
  return (
    <>
      <CareerProgression kind={kind} slug={slug} years={[year]} removals={removals} />
      <SeasonResults rows={rows} total={rows.length} kind={kind} />
    </>
  )
}

/**
 * A season picker, because a long career runs past any one page.
 *
 * Ferrari has 2,581 results and the newest come first, so without this the
 * 1950s would be unreachable. The years come from the seasons list rather than
 * from the results, which would be circular.
 */
function SeasonFilter({ year }: { year: number | null }) {
  const [params, setParams] = useSearchParams()
  const seasons = useApi<components['schemas']['Page_SeasonSummary_']>(['seasons'], () =>
    api.GET('/api/v1/seasons', { params: { query: { limit: 200 } } }),
  )
  const years = [...(seasons.data?.items ?? [])].sort((a, b) => b.year - a.year)

  return (
    <label className="search">
      <span className="muted">Season</span>
      <select
        value={year ?? ''}
        onChange={(event) => {
          // Everything else stays: a category's bans or points table live here too.
          const updated = new URLSearchParams(params)
          if (event.target.value) updated.set('year', event.target.value)
          else updated.delete('year')
          setParams(updated, { replace: true })
        }}
      >
        <option value="">Most recent</option>
        {years.map((season) => (
          <option key={season.year} value={season.year}>
            {season.year}
          </option>
        ))}
      </select>
    </label>
  )
}

function toYear(raw: string | null): number | null {
  return raw !== null && /^\d{4}$/.test(raw) ? Number(raw) : null
}

type CareerFigures = Pick<
  DriverDetail,
  | 'total_championship_wins'
  | 'total_race_starts'
  | 'total_race_wins'
  | 'total_podiums'
  | 'total_pole_positions'
  | 'total_points'
>

function Career({ entity }: { entity: CareerFigures }) {
  const stats: [string, string | number][] = [
    ['Championships', entity.total_championship_wins],
    ['Starts', entity.total_race_starts],
    ['Wins', entity.total_race_wins],
    ['Podiums', entity.total_podiums],
    ['Poles', entity.total_pole_positions],
    ['Points', formatPoints(entity.total_points, '0')],
  ]
  return (
    <dl className="stats">
      {stats.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  )
}

/** Results grouped by season. */
function SeasonResults({
  rows,
  total,
  kind,
}: {
  rows: ResultItem[]
  total: number
  kind: 'drivers' | 'constructors'
}) {
  const seasons = useMemo(() => groupByYear(rows), [rows])
  const to = useUniversePath()

  return (
    <>
      {rows.length < total ? (
        <p className="muted">
          Showing the {rows.length} most recent of {total.toLocaleString()} results. Pick a
          season above to see an earlier one.
        </p>
      ) : null}
      {seasons.map(([year, entries]) => (
        <div key={year} className="season-block">
          <h3 className="season-block__head">
            <Link to={to(`/seasons/${year}`)}>{year}</Link>
          </h3>
          <table className="grid">
            <thead>
              <tr>
                <th className="num">Rd</th>
                <th className="num">Pos</th>
                {kind === 'constructors' ? <th>Driver</th> : <th>Constructor</th>}
                <th className="num">Points</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((row, index) => (
                <tr key={`${row.round}-${row.driver.slug}-${index}`}>
                  <td className="num">
                    <Link to={to(`/races/${row.year}/${row.round}`)}>{row.round}</Link>
                    {row.session_type.startsWith('PRACTICE')
                      ? ` FP${row.session_type.slice(-1)}`
                      : null}
                  </td>
                  <td className="num">{row.position.number ?? row.position.text}</td>
                  <td className="muted">
                    {kind === 'constructors' ? row.driver.name : row.constructor.name}
                  </td>
                  <td className="num">{formatPoints(row.points, '')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </>
  )
}

function yearsOf(rows: ResultItem[]): number[] {
  return [...new Set(rows.map((row) => row.year))]
}

function groupByYear(rows: ResultItem[]): [number, ResultItem[]][] {
  const groups = new Map<number, ResultItem[]>()
  for (const row of rows) {
    groups.set(row.year, [...(groups.get(row.year) ?? []), row])
  }
  return [...groups.entries()].sort((a, b) => b[0] - a[0])
}
